import { useState } from 'react'

import { DataPreviewModal } from '@/components/judgments/DataPreviewModal'
import { Menu, MenuItem } from '@/components/ui/Menu'
import { useToast } from '@/hooks/useToast'
import { exportUrl, type SearchQuery } from '@/lib/apiClient'
import { cn } from '@/lib/cn'
import { downloadUrl } from '@/lib/download'
import { formatNumber } from '@/lib/format'

export interface ExportBarProps {
  query: Omit<SearchQuery, 'page' | 'pageSize'>
  totalItems: number
  disabled?: boolean
}

type Format = 'csv' | 'json' | 'xlsx'

const ITEMS: Array<{ format: Format; label: string; hint: string }> = [
  { format: 'csv', label: 'CSV', hint: 'Spreadsheets, one row per judgment' },
  { format: 'xlsx', label: 'Excel', hint: 'Judgments, parties and a report sheet' },
  { format: 'json', label: 'JSON', hint: 'Full records including party detail' },
]

const DownloadIcon = () => (
  <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" aria-hidden="true">
    <path
      d="M8 2.5v7.5M5 7.5L8 10.5l3-3M3 12.5h10"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

/**
 * Export control.
 *
 * Exporting is the last step of the task, so it gets one button rather than the
 * six that previously outweighed the search field. Every link is built from the
 * same filter state the table is rendering, so a download can never be a
 * differently filtered set than what was on screen.
 */
export function ExportBar({ query, totalItems, disabled }: ExportBarProps) {
  const [preview, setPreview] = useState<'csv' | 'json' | null>(null)
  const { push } = useToast()

  const start = (format: Format, close: () => void) => {
    downloadUrl(exportUrl(format, query))
    close()
    push({
      tone: 'success',
      title: `${format.toUpperCase()} download started`,
      description: `${formatNumber(totalItems)} record${totalItems === 1 ? '' : 's'}.`,
    })
  }

  const empty = disabled || totalItems === 0

  return (
    <>
      <Menu
        label="Export"
        triggerProps={{
          size: 'sm',
          variant: 'secondary',
          disabled: empty,
          icon: <DownloadIcon />,
        }}
        header={`${formatNumber(totalItems)} record${totalItems === 1 ? '' : 's'} matching the current filters`}
      >
        {(close) => (
          <>
            {ITEMS.map((item) => (
              <MenuItem
                key={item.format}
                onSelect={() => start(item.format, close)}
                description={item.hint}
              >
                Download {item.label}
              </MenuItem>
            ))}
            <div className="flex border-t border-line">
              {(['json', 'csv'] as const).map((format, index) => (
                <button
                  key={format}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setPreview(format)
                    close()
                  }}
                  className={cn(
                    'flex-1 px-3 py-2 text-sm text-ink-secondary transition-colors',
                    'hover:bg-surface-hover hover:text-ink',
                    'focus-visible:bg-surface-hover focus-visible:outline-none',
                    index === 0 && 'border-r border-line',
                  )}
                >
                  Preview {format.toUpperCase()}
                </button>
              ))}
            </div>
          </>
        )}
      </Menu>

      <DataPreviewModal
        format={preview}
        query={query}
        totalItems={totalItems}
        onClose={() => setPreview(null)}
      />
    </>
  )
}
